import { useDeleteSettlement } from '@services/settlements/mutations/useDeleteSettlement'
import { usePublishSettlement } from '@services/settlements/mutations/usePublishSettlement'
import { useUnpublishSettlement } from '@services/settlements/mutations/useUnpublishSettlement'
import { useSettlementPdf } from '@services/settlements/queries/useSettlementPdf'
import type { SettlementDetail } from '@services/settlements/types'
import { FileDown, Send, Trash2, Undo2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

import { formatDate } from './format'

export function SettlementStatusBar({
  settlement
}: {
  settlement: SettlementDetail
}) {
  const { t } = useTranslation('settlement')
  const { confirm, dialog } = useConfirm()
  const navigate = useNavigate()

  const publish = usePublishSettlement()
  const unpublish = useUnpublishSettlement()
  const remove = useDeleteSettlement()
  const pdf = useSettlementPdf()

  const [overrideNote, setOverrideNote] = useState('')

  const published = settlement.status === 'PUBLISHED'
  const hasErrors = settlement.checks.some(
    (check) => check.severity === 'ERROR'
  )

  const onPublish = async () => {
    const confirmed = await confirm({
      title: t('publish.confirm_title'),
      description: t('publish.confirm_body')
    })
    if (!confirmed) return

    publish.mutate({
      id: settlement.id,
      body: hasErrors
        ? { force: true, override_note: overrideNote }
        : undefined
    })
  }

  const onUnpublish = async () => {
    if (
      await confirm({
        title: t('publish.unpublish_confirm'),
        destructive: true
      })
    ) {
      unpublish.mutate(settlement.id)
    }
  }

  const onDelete = async () => {
    const confirmed = await confirm({
      title: t('delete.confirm_title'),
      description: t('delete.confirm_body'),
      destructive: true,
      confirmText: t('actions.delete')
    })
    if (!confirmed) return

    remove.mutate(settlement.id, {
      onSuccess: () =>
        navigate(`/admin/flats/${settlement.flat.id}?tab=settlements`)
    })
  }

  return (
    <Card className="flex flex-wrap items-center justify-between gap-4 p-4">
      <div>
        <p className="m-0 flex items-center gap-2 font-display text-lg font-bold text-ink">
          {settlement.title}
          <span
            className={cn(
              'rounded-full px-2.5 py-0.5 text-xs font-semibold',
              published
                ? 'bg-success-soft text-success'
                : 'bg-ink/[0.06] text-muted-foreground'
            )}
          >
            {t(`status.${settlement.status}`)}
          </span>
        </p>
        <p className="m-0 text-sm text-muted-foreground">
          {formatDate(settlement.period_start)} –{' '}
          {formatDate(settlement.period_end)}
          {published && settlement.published_at && (
            <> · {t('list.published_at')} {formatDate(settlement.published_at)}</>
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {!published && hasErrors && (
          <Input
            className="w-56"
            placeholder={t('publish.override_note')}
            value={overrideNote}
            onChange={(event) => setOverrideNote(event.target.value)}
          />
        )}

        <Button
          variant="outline"
          onClick={() => pdf.mutate(settlement.id)}
          disabled={pdf.isPending}
        >
          <FileDown className="mr-1.5 h-4 w-4" />
          {t('pdf.download')}
        </Button>

        {published ? (
          <Button
            variant="outline"
            onClick={onUnpublish}
            disabled={unpublish.isPending}
          >
            <Undo2 className="mr-1.5 h-4 w-4" />
            {t('publish.unpublish')}
          </Button>
        ) : (
          <>
            {/* Only a draft can be deleted; a published settlement has to be
                returned to draft first. */}
            <Button
              variant="outline"
              onClick={onDelete}
              disabled={remove.isPending}
            >
              <Trash2 className="mr-1.5 h-4 w-4 text-destructive" />
              {t('actions.delete')}
            </Button>
            <Button
              onClick={onPublish}
              disabled={publish.isPending || (hasErrors && !overrideNote.trim())}
            >
              <Send className="mr-1.5 h-4 w-4" />
              {hasErrors ? t('publish.force') : t('publish.action')}
            </Button>
          </>
        )}
      </div>

      {!published && hasErrors && (
        <p className="m-0 w-full text-sm text-destructive">
          {t('publish.has_errors')}
        </p>
      )}

      {dialog}
    </Card>
  )
}
